<template>
  <activity-item-wrapper
    :type="CommunicationType.note"
    :class="{ 'highlight-note': editingNote }"
    :actions
    :is-pinned
  >
    <template #subIcon>
      <div
        v-if="subIcon"
        class="absolute -right-1 -bottom-1 flex h-3-5 w-3-5 items-center justify-center rounded-full border-2 border-white"
        :class="subIcon.bgColor"
      >
        <icon-base
          :icon="subIcon.icon"
          width="6"
          height="6"
          view-box="0 0 16 16"
          class="!-mb-px text-white"
        />
      </div>
    </template>
    <template #content>
      <div class="flex w-full items-center justify-between">
        <note-status :note="item" />
        <button
          v-if="isPinned"
          class="ml-auto"
          @click="pinNoteAction(item.id, false)"
        >
          <icon-base :icon="IconPin" class="text-primary" />
        </button>
      </div>
      <activity-text
        v-if="isEditing"
        :text="item.comment"
        :is-loading="isUpdating"
        :placeholder="$t('ACTIVITY.TASK.TASK_DESCRIPTION')"
        @submit="updateNote"
        @cancel="isEditing = false"
      />
      <activity-content v-else :content="item.comment" />
      <note-footer :note="item" />
    </template>
  </activity-item-wrapper>
</template>
<script setup lang="ts">
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";

import { CommunicationType } from "@/enums/communicationLogs";
import { FullNotableType, NotableType } from "@/enums/dealActivity";
import { useAuth } from "@/hooks/auth";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import { usePromiseWrapper } from "@/hooks/common";
import { useDealsBase } from "@/hooks/deals";
import { useUpdateNoteMutation } from "@/hooks/queries/useInbox";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";
import notesService from "@/services/modules/notes";
import { useCommunicationStore } from "@/stores/communication";
import usePiPStore from "@/stores/pip";

import ActivityContent from "@/components/deals/dealActivity/ActivityContent.vue";
import ActivityItemWrapper from "@/components/deals/dealActivity/ActivityItemWrapper.vue";
import ActivityText from "@/components/deals/dealActivity/ActivityText.vue";
import NoteFooter from "@/components/deals/dealActivity/notes/NoteFooter.vue";
import NoteStatus from "@/components/deals/dealActivity/notes/NoteStatus.vue";
import IconBusiness from "@/components/icons/IconBusiness.vue";
import IconDelete from "@/components/icons/IconDelete.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import IconLink from "@/components/icons/IconLink.vue";
import IconPin from "@/components/icons/IconPinSecondary.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { NoteLogData } from "@/models/communicationLogs";
import type { ActivityAction } from "@/models/dealActivity";
import type { Note, NotePayload } from "@/models/notes";
import type { IUser } from "@/models/users";

const { item, editingNote } = defineProps<{
  item: NoteLogData;
  editingNote: boolean;
}>();

const emit = defineEmits<{
  "note:edit": [];
  "note:deleted": [];
  "note:pin-changed": [];
}>();

const { activeDeal } = useDealsBase();
const { activePiPDeal } = storeToRefs(usePiPStore());
const router = useRouter();
const copyToClipboard = useClipboardWithMessage();
const { getters } = useStore();
const communicationStore = useCommunicationStore();
const { isAdmin } = useAuth();
const { dealActivityActiveTypes } = storeToRefs(communicationStore);
const { mutateAsync: updateNoteMutation, isPending: isUpdating } =
  useUpdateNoteMutation(true);

const isEditing = ref(false);

const { fetchWrapper: pinNoteAction } = usePromiseWrapper(
  async (id: Note["id"], isPinned: boolean) => {
    await notesService.pinNote(id, isPinned);
    emit("note:pin-changed");
  }
);

const user = computed<IUser | null>(() => getters["auth/user"]);

const subIcon = computed(() => {
  const options = {
    [FullNotableType.business]: {
      icon: IconBusiness,
      bgColor: "bg-secondary-purple-1",
    },
    [FullNotableType.call]: {
      icon: IconTelephone,
      bgColor: "bg-woods-green",
    },
  };

  return options[item.notable_type as keyof typeof options] ?? null;
});

const isNoteOnlyType = computed(
  () =>
    dealActivityActiveTypes.value.length === 1 &&
    dealActivityActiveTypes.value[0] === CommunicationType.note
);

const isPinned = computed(
  () =>
    !!item.pinned_notes?.some(
      ({ note_id, user_id }) =>
        note_id === item.id && user_id === user.value?.id
    )
);

const actions = computed(() => {
  const options: ActivityAction[] = [
    {
      name: "link",
      icon: IconLink,
      viewBox: "2 4 20 20",
      class: "text-label !-mb-1 hover:text-primary",
      click: () => {
        copyNoteLink(item.id);
      },
    },
  ];

  if (user.value?.id === item.user.id || isAdmin) {
    options.unshift({
      name: "edit",
      icon: IconEdit,
      viewBox: "0 0 18 18",
      class: "text-label hover:text-primary",
      click: () => {
        isNoteOnlyType.value ? emit("note:edit") : (isEditing.value = true);
      },
    });

    options.push({
      name: "delete",
      icon: IconDelete,
      class: "text-label hover:text-error",
      click: () => {
        deleteNote(item.id);
      },
    });
  }

  if (!isPinned.value) {
    options.unshift({
      name: "pin",
      icon: IconPin,
      class: isPinned.value ? "text-primary" : "text-label hover:text-primary",
      click: () => {
        pinNoteAction(item.id, true);
      },
    });
  }

  return options;
});

const updateNote = async (noteText: string) => {
  if (item.comment === noteText) {
    isEditing.value = false;
    return;
  }

  const payload: NotePayload = {
    id: item.id,
    notable_type: NotableType[item.notable_type],
    notable_id: item.notable_id,
    comment: noteText,
    visibility_scope: item.visibility_scope,
  };

  if (item.stage && item.visible_in_deal_list) {
    payload.stage = item.stage;
    payload.visible_in_deal_list = item.visible_in_deal_list;
  }

  if (item.call_disposition) {
    payload.call_disposition = item.call_disposition;
  }

  await updateNoteMutation(payload);
  isEditing.value = false;
};

const copyNoteLink = (id: number) => {
  const origin = location?.origin || "";
  const dealId = activePiPDeal.value
    ? activePiPDeal.value?.id
    : activeDeal.value?.id;
  const path = router.resolve({
    name: ROUTE_DEAL_PROGRESS,
    params: { dealId },
    query: { note: id },
  }).href;

  copyToClipboard(`${origin}${path}`, {
    isPiP: !!activePiPDeal.value,
  });
};

const deleteNote = async (id: number) => {
  await notesService.deleteNote(id);
  emit("note:deleted");
};
</script>
<style>
@reference "@/assets/tailwind.css";

.highlight-note {
  @apply rounded-sm border border-primary bg-surface-user-blue;
}
</style>
